// --- Space Weather Short Widget Components ---
import { useState, useEffect, useMemo, useCallback, useRef, type ReactNode } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { addDays, format, formatDistanceToNow, startOfMonth, endOfMonth } from 'date-fns';
import {
  Activity,
  Bell,
  Calendar,
  CalendarClock,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Circle,
  Clock,
  Grid3X3,
  Link2,
  Link2Off,
  List,
  Moon,
  Settings,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AiDaySummaryWidget } from '@/components/dashboard/AiDaySummaryWidget';
import { SpaceWeatherWidget } from '@/components/dashboard/SpaceWeatherWidget';
import { SpaceWeatherWaveformWidget } from '@/components/dashboard/SpaceWeatherWaveformWidget';
import { DriftView } from '@/components/calendar/DriftView';
import { GregorianGrid } from '@/components/calendar/GregorianGrid';
import { MonthGrid } from '@/components/calendar/MonthGrid';
import { MonthHeader } from '@/components/calendar/MonthHeader';
import { MoonPhase } from '@/components/calendar/MoonPhase';
import { YearRing } from '@/components/calendar/YearRing';
import { CycleGraph, type TimeScale } from '@/components/calendar/CycleGraph';
import { MultiRing } from '@/components/calendar/MultiRing';
import CycleLegend from '@/components/calendar/CycleLegend';
import { SettingsPanel } from '@/components/calendar/SettingsPanel';
import { AgendaWidget } from '@/components/calendar/AgendaWidget';
import { RemindersWidget } from '@/components/calendar/RemindersWidget';
import { ShortWidgetWrapper } from '@/components/skins/ShortWidgetWrapper';
import { ShortCyclesWidget } from '@/components/skins/ShortCyclesWidget';
import { ShortWaveformWidget } from '@/components/skins/ShortWaveformWidget';
import { ShortLegendWidget } from '@/components/skins/ShortLegendWidget';
import { ShortGridWidget } from '@/components/skins/ShortGridWidget';
import { cn } from '@/lib/utils';
import { useTallRowExpand } from '@/lib/tallRowContext';
import { db } from '@/lib/firebase';
import { usePaulAccess } from '@/lib/paulAccess';
import { EventBadge } from '@/components/calendar/EventBadge';
import { useEventsForDay, useEventsForRange, useEventPriorityBadge } from '@/hooks/useEventsForRange';

import type { WidgetConfig } from '@/components/dashboard/types';
import type { WidgetCustomizationState } from '@/lib/dashboardTypes';
import type { Month13 } from '@/lib/calendar13Types';
import type { Day13, Calendar13Config, LiveGalacticDay, IndicatorDay, ActiveRingTheme } from './dashboardHooksShared';
import type { CycleRing } from '@/lib/cycleTypes';
import type { CycleAlignment } from '@/lib/cycleConfig';
import type { RingThemeState } from '@/lib/ringThemeStore';

type SpaceWeatherReport = {
  product_id?: string;
  issue_datetime?: string;
  message?: string;
  forecast?: unknown;
  pubDate?: string;
};

// Extend window for debug counters
declare global {
  interface Window {
    __SPACE_WEATHER_SHORT_RENDERED?: number;
    __SPACE_WEATHER_SHORT_TALL_RENDERED?: number;
  }
}

function ShortSpaceWeatherWidget() {
  const [latest, setLatest] = useState<SpaceWeatherReport | null>(null);
  const [recentCount, setRecentCount] = useState(0);
  useEffect(() => {
    window.__SPACE_WEATHER_SHORT_RENDERED = (window.__SPACE_WEATHER_SHORT_RENDERED || 0) + 1;
    // eslint-disable-next-line no-console
    console.warn('[DEBUG] SpaceWeatherWidget SHORT RENDERED', window.__SPACE_WEATHER_SHORT_RENDERED, new Date().toISOString());
    // Listen for latest report and count in last 7 days
    const baseRef = collection(db, 'spaceWeatherReports');
    const q = query(baseRef, orderBy('issue_datetime', 'desc'));
    const unsubscribe = onSnapshot(q, snap => {
      let latestItem = null;
      let count = 0;
      const now = new Date();
      snap.forEach(doc => {
        const data = doc.data();
        if (!latestItem) latestItem = data;
        // Count if within last 7 days
        const dt = data.issue_datetime ? new Date(data.issue_datetime) : (data.pubDate ? new Date(data.pubDate) : null);
        if (dt && (now.getTime() - dt.getTime()) < 7 * 24 * 60 * 60 * 1000) {
          count++;
        }
      });
      setLatest(latestItem);
      setRecentCount(count);
    });
    return () => unsubscribe();
  }, []);
  const issuedAt = latest?.issue_datetime ? new Date(latest.issue_datetime) : latest?.pubDate ? new Date(latest.pubDate) : null;
  const issuedAgo = issuedAt && !isNaN(issuedAt.getTime()) ? formatDistanceToNow(issuedAt, { addSuffix: true }) : null;
  const headline = latest?.message ? latest.message.slice(0, 90) + (latest.message.length > 90 ? '…' : '') : 'No recent message';
  const product = latest?.product_id ?? 'Alert';

  return (
    <div
      className="w-full h-full flex flex-col gap-2 rounded-xl border border-blue-300/20 bg-gradient-to-br from-slate-950/70 via-slate-900/60 to-cyan-900/60 p-3 shadow-[0_10px_24px_rgba(0,0,0,0.35)]"
      style={{ position: 'relative' }}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-blue-100">
          <span role="img" aria-label="space">🛰️</span>
          <span className="text-sm font-semibold">Space Weather</span>
        </div>
        <span className="text-[11px] text-blue-200/70">{recentCount} alerts · 7d</span>
      </div>

      <div className="flex items-center gap-2 text-[11px] text-blue-100/80">
        <span className="px-2 py-0.5 rounded-full bg-blue-500/15 border border-blue-300/30 text-blue-100 font-semibold uppercase tracking-tight text-[10px]">
          {product}
        </span>
        {issuedAgo && <span className="text-blue-200/70">Updated {issuedAgo}</span>}
      </div>

      <div className="flex-1 flex items-center text-sm text-blue-50 leading-snug text-center">
        {headline}
      </div>

      {latest?.forecast && (
        <div className="text-[11px] text-cyan-100/80 truncate">
          Forecast: {typeof latest.forecast === 'string' ? latest.forecast : JSON.stringify(latest.forecast)}
        </div>
      )}
    </div>
  );
}

function ShortTallSpaceWeatherWidget() {
  const [latest, setLatest] = useState<SpaceWeatherReport | null>(null);
  const [recentCount, setRecentCount] = useState(0);

  useEffect(() => {
    window.__SPACE_WEATHER_SHORT_TALL_RENDERED = (window.__SPACE_WEATHER_SHORT_TALL_RENDERED || 0) + 1;
    // eslint-disable-next-line no-console
    console.warn('[DEBUG] SpaceWeatherWidget SHORT TALL RENDERED', window.__SPACE_WEATHER_SHORT_TALL_RENDERED, new Date().toISOString());
    const baseRef = collection(db, 'spaceWeatherReports');
    const q = query(baseRef, orderBy('issue_datetime', 'desc'));
    const unsubscribe = onSnapshot(q, snap => {
      let latestItem = null;
      let count = 0;
      const now = new Date();
      snap.forEach(doc => {
        const data = doc.data();
        if (!latestItem) latestItem = data;
        const dt = data.issue_datetime ? new Date(data.issue_datetime) : (data.pubDate ? new Date(data.pubDate) : null);
        if (dt && (now.getTime() - dt.getTime()) < 7 * 24 * 60 * 60 * 1000) {
          count++;
        }
      });
      setLatest(latestItem);
      setRecentCount(count);
    });
    return () => unsubscribe();
  }, []);

  const issuedAt = latest?.issue_datetime ? new Date(latest.issue_datetime) : latest?.pubDate ? new Date(latest.pubDate) : null;
  const issuedAgo = issuedAt && !isNaN(issuedAt.getTime()) ? formatDistanceToNow(issuedAt, { addSuffix: true }) : null;
  const headline = latest?.message ? latest.message.slice(0, 120) + (latest.message.length > 120 ? '…' : '') : 'No recent message';
  const product = latest?.product_id ?? 'Alert';

  return (
    <div
      className="flex flex-col gap-2 text-blue-50 text-sm font-semibold rounded-xl border border-blue-300/20 bg-gradient-to-br from-slate-950/70 via-slate-900/60 to-cyan-900/60 p-3 shadow-[0_12px_28px_rgba(0,0,0,0.35)]"
      style={{ position: 'relative' }}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-blue-100">
          <span role="img" aria-label="space">🛰️</span>
          <span className="text-sm font-semibold">Space Weather</span>
        </div>
        <span className="text-[11px] text-blue-200/70">{recentCount} alerts · 7d</span>
      </div>

      <div className="flex items-center gap-2 text-[11px] text-blue-100/80">
        <span className="px-2 py-0.5 rounded-full bg-blue-500/15 border border-blue-300/30 text-blue-100 font-semibold uppercase tracking-tight text-[10px]">
          {product}
        </span>
        {issuedAgo && <span className="text-blue-200/70">Updated {issuedAgo}</span>}
      </div>

      <div className="flex-1 flex items-center text-sm text-blue-50 leading-snug text-center">
        {headline}
      </div>

      {latest?.forecast && (
        <div className="text-[11px] text-cyan-100/80 line-clamp-2">
          Forecast: {typeof latest.forecast === 'string' ? latest.forecast : JSON.stringify(latest.forecast)}
        </div>
      )}
    </div>
  );
}

const GregorianShortCalendar = ({
  monthLabel,
  days,
  selectedDate,
  todayDate,
  onSelect,
  dashboardId,
}: {
  monthLabel: string;
  days: Date[];
  selectedDate: Date | null;
  todayDate: Date | null;
  onSelect: (day: Date) => void;
  dashboardId?: string;
}) => {
  const [open, setOpen] = useState(false);
  const [dateInput, setDateInput] = useState<string>(() => {
    const base = selectedDate ?? todayDate;
    return base ? format(base, 'yyyy-MM-dd') : '';
  });
  const rowRef = useRef<HTMLDivElement>(null);
  const lastScrollLeftRef = useRef<number | null>(null);
  
  // Fetch events for the visible month
  const monthStart = days.length > 0 ? startOfMonth(days[0]) : new Date();
  const monthEnd = days.length > 0 ? endOfMonth(days[days.length - 1]) : new Date();
  const eventsByDate = useEventsForRange(monthStart, monthEnd, dashboardId);

  useEffect(() => {
    const base = selectedDate ?? todayDate;
    setDateInput(base ? format(base, 'yyyy-MM-dd') : '');
  }, [selectedDate, todayDate]);

  useEffect(() => {
    if (!rowRef.current) return;
    const targetDate = selectedDate ?? todayDate;
    if (!targetDate) return;
    const key = format(targetDate, 'yyyy-MM-dd');
    const currentEl = rowRef.current.querySelector<HTMLButtonElement>(`button[data-date="${key}"]`);
    if (!currentEl) return;

    const scrollParent = rowRef.current;
    const containerWidth = scrollParent.clientWidth;
    const nextKey = format(addDays(new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate()), 1), 'yyyy-MM-dd');
    const nextEl = rowRef.current.querySelector<HTMLButtonElement>(`button[data-date="${nextKey}"]`);
    const currentCenter = currentEl.offsetLeft + currentEl.offsetWidth / 2;
    const nextCenter = nextEl ? nextEl.offsetLeft + nextEl.offsetWidth / 2 : currentCenter;
    const millisecondsIntoDay =
      targetDate.getHours() * 60 * 60 * 1000 +
      targetDate.getMinutes() * 60 * 1000 +
      targetDate.getSeconds() * 1000 +
      targetDate.getMilliseconds();
    const dayProgress = Math.max(0, Math.min(1, millisecondsIntoDay / (24 * 60 * 60 * 1000)));
    const targetCenter = currentCenter + (nextCenter - currentCenter) * dayProgress;
    const targetScrollLeft = Math.max(0, targetCenter - containerWidth / 2);

    if (lastScrollLeftRef.current !== null && Math.abs(lastScrollLeftRef.current - targetScrollLeft) < 0.5) {
      return;
    }

    scrollParent.scrollLeft = targetScrollLeft;
    lastScrollLeftRef.current = targetScrollLeft;
  }, [selectedDate, todayDate]);

  return (
    <ShortWidgetWrapper>
      <div className="relative flex h-full w-full flex-col justify-center px-1 py-0.5">
        <div className="pointer-events-none absolute left-1.5 top-0.5 text-[9px] font-medium tracking-[0.08em] text-white/42">
          {monthLabel}
        </div>
        <div className="absolute right-1 top-0 z-10">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-4.5 w-4.5 rounded-full border-white/10 bg-white/[0.04] p-0 text-white/65 hover:bg-white/[0.08] hover:text-white"
            onClick={() => setOpen(true)}
            title="Change date"
          >
            <CalendarClock className="h-2.5 w-2.5" />
          </Button>
        </div>

        <div ref={rowRef} className="flex flex-1 min-h-0 items-center gap-1 overflow-x-auto pt-3 pb-0">
          {days.map((day) => {
            const isToday = todayDate && day.getDate() === todayDate.getDate() && day.getMonth() === todayDate.getMonth() && day.getFullYear() === todayDate.getFullYear();
            const isSelected = selectedDate && day.getDate() === selectedDate.getDate() && day.getMonth() === selectedDate.getMonth() && day.getFullYear() === selectedDate.getFullYear();
            const showSelected = isSelected;
            const weekdayLabel = format(day, 'EEE').toUpperCase();
            const dateKey = day.toISOString().split('T')[0];
            const dayEvents = eventsByDate[dateKey] || [];
            const { color, count, statuses } = useEventPriorityBadge(dayEvents);
            
            return (
              <button
                key={day.toISOString()}
                data-date={format(day, 'yyyy-MM-dd')}
                onClick={() => onSelect(day)}
                className={cn(
                  "relative flex items-center justify-center gap-1 rounded-lg text-[12px] px-2.5 py-1.5 transition-all duration-200 whitespace-nowrap font-medium shrink-0",
                  showSelected
                    ? "bg-white text-slate-900 border border-slate-200 shadow-[0_6px_18px_rgba(0,0,0,0.12)] ring-1 ring-slate-100 overflow-hidden"
                    : isToday
                      ? "border border-cyan-400/40 bg-cyan-500/15 text-cyan-50"
                      : "border border-border/50 bg-background/60 text-foreground/70 hover:text-foreground hover:bg-secondary/50"
                )}
                style={{ 
                  minHeight: '38px',
                  transform: showSelected ? 'scale(1.08)' : 'scale(1)',
                  zIndex: showSelected ? 10 : 0,
                  paddingTop: showSelected ? '7px' : undefined,
                }}
              >
                {showSelected && (
                  <span className="absolute inset-x-0 top-0 h-4 text-[9px] font-semibold tracking-[0.18em] text-white bg-gradient-to-b from-[#d92a2a] to-[#c32020] shadow-[0_2px_4px_rgba(0,0,0,0.18)] flex items-center justify-center uppercase">
                    {weekdayLabel}
                  </span>
                )}
                {day.getDate()}
                <EventBadge color={color} count={count} statuses={statuses} />
              </button>
            );
          })}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Select Gregorian date</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <input
              type="date"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={dateInput}
              onChange={(e) => setDateInput(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  if (!dateInput) return setOpen(false);
                  const parsed = new Date(dateInput + 'T12:00:00');
                  if (isNaN(parsed.getTime())) return setOpen(false);
                  onSelect(parsed);
                  setOpen(false);
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </ShortWidgetWrapper>
  );
}
export function SpaceWeatherWidgetCard({
  timelineDate,
  isVisible,
}: {
  timelineDate: Date;
  isVisible: boolean;
}) {
  return <SpaceWeatherWidget timelineDate={timelineDate} isVisible={isVisible} />;
}

// Timeline widget component that updates every second when in live mode
function TimelineWidget({
  currentGregorianDate,
  isManualMode,
  handleSyncToNowFromTimeline,
}: {
  currentGregorianDate: Date;
  isManualMode: boolean;
  handleSyncToNowFromTimeline: () => void;
}) {
  const [, setSecondTick] = useState(0);

  useEffect(() => {
    if (isManualMode) return; // Only update in live mode

    const interval = setInterval(() => {
      setSecondTick((t) => t + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isManualMode]);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between h-full w-full max-w-6xl mx-auto p-2 sm:p-4 gap-2 sm:gap-0">
      <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
        <div className="h-10 w-10 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
          <Clock className="h-4 w-4 text-primary" />
        </div>
        <div className="text-center sm:text-left">
          <p className="text-sm sm:text-base font-semibold">
            {currentGregorianDate && !isNaN(currentGregorianDate.getTime()) ? format(currentGregorianDate, 'EEEE, MMMM d, yyyy') : '—'} •{' '}
            {currentGregorianDate && !isNaN(currentGregorianDate.getTime()) ? format(currentGregorianDate, 'HH:mm:ss') : '—'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-2 sm:mt-0 w-full sm:w-auto justify-center sm:justify-end">
        {/* NOW button: highlighted (amber) when timeline MATCHES actual current time */}
        {(() => {
          const now = new Date();
          const isMatch = currentGregorianDate && 
            currentGregorianDate.getFullYear() === now.getFullYear() &&
            currentGregorianDate.getMonth() === now.getMonth() &&
            currentGregorianDate.getDate() === now.getDate() &&
            Math.abs(currentGregorianDate.getHours() - now.getHours()) === 0 &&
            Math.abs(currentGregorianDate.getMinutes() - now.getMinutes()) === 0;
          
          return (
            <Button 
              variant={isMatch ? "default" : "outline"}
              size="sm" 
              className={`w-full sm:w-auto ${
                isMatch
                  ? 'border-amber-300/70 bg-amber-200/70 text-amber-950 hover:bg-amber-200/80 hover:text-amber-950'
                  : ''
              }`}
              onClick={handleSyncToNowFromTimeline}
            >
              Now
            </Button>
          );
        })()}
      </div>
    </div>
  );
}


function GregorianGridHeader({
  monthLabel,
  monthFraction,
  onPrev,
  onNext,
}: {
  monthLabel: string;
  monthFraction: string;
  onPrev: () => void;
  onNext: () => void;
}) {
  const tallRow = useTallRowExpand();
  return (
    <div className="mb-0.5 flex items-center justify-between gap-1 px-0 py-0">
      <Button
        variant="ghost"
        size="icon"
        onClick={onPrev}
        className="h-6 w-6 min-w-6 rounded-full border border-white/8 bg-white/[0.04] text-white/68 hover:bg-white/[0.08] hover:text-white"
      >
        <ChevronLeft className="h-3 w-3" />
      </Button>
      <div className="text-center flex-1 min-w-0 flex items-center justify-center gap-2">
        <h2 className="truncate text-[clamp(0.8rem,2vw,0.95rem)] font-display font-semibold tracking-[0.04em] text-white">
          {monthLabel}
        </h2>
        <span className="shrink-0 text-[0.65rem] font-mono tabular-nums text-white/35 tracking-tight border-l border-white/15 pl-2">
          {monthFraction}
        </span>
      </div>
      <div className="flex items-center gap-0">
        {tallRow && (
          <Button
            variant="ghost"
            size="icon"
            onClick={tallRow.toggleTallExpanded}
            className="h-6 w-6 min-w-6 rounded-full border border-white/8 bg-white/[0.04] text-white/68 hover:bg-white/[0.08] hover:text-white"
          >
            {tallRow.isTallExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={onNext}
          className="h-6 w-6 min-w-6 rounded-full border border-white/8 bg-white/[0.04] text-white/68 hover:bg-white/[0.08] hover:text-white"
        >
          <ChevronRight className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

export function useDashboardWidgets({
  currentGregorianDate,
  isManualMode,
  shownTime,
  displayTime,
  currentTime,
  displayDay,
  liveGalacticDay,
  shouldUseLiveDay,
  selectedDay,
  selectedMonth,
  selectedYear,
  currentMonth,
  allMonths,
  today,
  indicatorDay,
  gregorianViewMonth,
  waveformDayOfYear,
  viewId,
  config,
  updateConfig,
  cycleDocId,
  cycleRings,
  hiddenRings,
  alignments,
  ringThemeState,
  activeRingTheme,
  cycleDisplayMode,
  widgetVisibleCount,
  setWidgetVisibleCount,
  setShowCycleWizard,
  setShowRingThemeWizard,
  setShowAlignmentTool,
  setAlignmentInitialCycleId,
  selectedGroupId,
  isAdmin,
  maxWidgetRings,
  widgetSyncModes,
  widgetCustomizations,
  isolatedWidgetValues,
  visibleWidgets,
  releaseWidgetMaster,
  nextMonth,
  prevMonth,
  handleSyncToNowFromGrid,
  handleSyncToNowFromTimeline,
  handleSyncToNowFromCycles,
  handleSyncToDriftFromGrid,
  handleSyncToDrift,
  handleGridPrevMonth,
  handleGridNextMonth,
  handleSelectDayFromGrid,
  handleSelectGregorianDateFromGrid,
  handleGregorianPrevMonth,
  handleGregorianNextMonth,
  handleRemoveCycle,
  handleEditCycle,
  handleRotationChangeFromCycles,
  handleToggleRing,
  handleAddRingFromPreset,
  handleRingsChange,
  handleReorderRings,
  handleSetSkinMode,
  handleWaveformChangeFromWidget,
  handleWaveformInteractionStart,
  handleYearRingSelectMonth,
  isYearRingSynced,
  onToggleYearRingSync,
  onCycleDisplayModeChange,
}: {
  currentGregorianDate: Date;
  isManualMode: boolean;
  shownTime: Date;
  displayTime: Date | null;
  currentTime: Date;
  displayDay: Day13;
  liveGalacticDay: LiveGalacticDay | null;
  shouldUseLiveDay: boolean;
  selectedDay: Day13 | null;
  selectedMonth: number;
  selectedYear: number;
  currentMonth: Month13 | null;
  allMonths: Month13[];
  today: Day13;
  indicatorDay: IndicatorDay;
  gregorianViewMonth: Date;
  waveformDayOfYear: number;
  viewId: string;

  config: Calendar13Config;
  updateConfig: (patch: Partial<Calendar13Config>) => void;
  cycleDocId: string;

  cycleRings: CycleRing[];
  hiddenRings: string[];
  alignments: CycleAlignment[];
  ringThemeState: RingThemeState;
  activeRingTheme: ActiveRingTheme;
  cycleDisplayMode: 'single' | 'multi';

  widgetVisibleCount: number | null;
  setWidgetVisibleCount: (count: number | null) => void;
  setShowCycleWizard: (open: boolean) => void;
  setShowRingThemeWizard: (open: boolean) => void;
  setShowAlignmentTool: (open: boolean) => void;
  setAlignmentInitialCycleId: (id: string | undefined) => void;
  selectedGroupId: string | null;

  isAdmin: boolean;
  maxWidgetRings: number;

  widgetSyncModes: Record<string, 'linked' | 'isolated' | 'fixed' | undefined>;
  widgetCustomizations: Record<string, WidgetCustomizationState>;
  isolatedWidgetValues: Record<string, number>;
  visibleWidgets: string[];

  releaseWidgetMaster: () => void;

  nextMonth: () => void;
  prevMonth: () => void;

  handleSyncToNowFromGrid: () => void;
  handleSyncToNowFromTimeline: () => void;
  handleSyncToNowFromCycles: () => void;
  handleSyncToDriftFromGrid: () => void;
  handleSyncToDrift: () => void;

  handleGridPrevMonth: () => void;
  handleGridNextMonth: () => void;

  handleSelectDayFromGrid: (day: Day13) => void;
  handleSelectGregorianDateFromGrid: (day: Date) => void;

  handleGregorianPrevMonth: () => void;
  handleGregorianNextMonth: () => void;

  handleRemoveCycle: (cycleId: string) => void;
  handleEditCycle: (cycle: CycleRing) => void;

  handleRotationChangeFromCycles: (dayOfYear: number, fractionOfDay: number) => void;

  handleToggleRing: (ringId: string) => void;
  handleAddRingFromPreset: (preset: unknown) => void;

  handleRingsChange: (rings: CycleRing[]) => void;
  handleReorderRings: (ringIds: string[]) => void;
  handleSetSkinMode: (ringId: string, mode: 'primary' | 'secondary' | 'tertiary' | null) => void;

  handleWaveformChangeFromWidget: (day: number, fractionOfDay: number) => void;
  handleWaveformInteractionStart: () => void;

  handleYearRingSelectMonth: (month: number) => void;
  isYearRingSynced: boolean;
  onToggleYearRingSync: () => void;
  onCycleDisplayModeChange: (mode: 'single' | 'multi') => void;
}) {
  const { isPaul } = usePaulAccess();

  const [yearRingManualMonth, setYearRingManualMonth] = useState<number | null>(null);

  useEffect(() => {
    if (!currentMonth && selectedMonth !== 14) {
      console.warn('[DashboardWidgets] currentMonth missing', {
        selectedMonth,
        selectedYear,
        displayDay,
      });
      return;
    }

    if (currentMonth && !currentMonth.name) {
      console.warn('[DashboardWidgets] currentMonth missing name', {
        selectedMonth,
        selectedYear,
        currentMonth,
      });
    }
  }, [currentMonth, displayDay, selectedMonth, selectedYear]);

  useEffect(() => {
    const invalidRings = cycleRings.filter((ring) => !ring || !ring.id);
    if (invalidRings.length) {
      console.warn('[DashboardWidgets] invalid cycle rings detected', {
        count: invalidRings.length,
        invalidRings,
      });
    }
  }, [cycleRings]);

  const safeCycleRings = useMemo(
    () => cycleRings.filter((ring) => ring && ring.id),
    [cycleRings],
  );
  const displayCycleRings = useMemo(
    () => safeCycleRings.filter((ring) => !hiddenRings.includes(ring.id)),
    [safeCycleRings, hiddenRings],
  );
  const interlockingCycleRings = useMemo(() => {
    // MultiRing sorts ascending by displayOrder, then renders index 0 as outermost.
    // Set displayOrder explicitly: lunar=0 (outer), moons=1 (middle), days=2 (inner).
    const WIDGET_ORDER: Record<string, number> = { lunar: 0, moons: 1, days: 2 };
    const find = (id: string) => safeCycleRings.find((r) => r.id === id);
    return (['lunar', 'moons', 'days'] as const)
      .map((id) => find(id))
      .filter((r): r is NonNullable<typeof r> => r != null)
      .map((r) => ({ ...r, displayOrder: WIDGET_ORDER[r.id] }));
  }, [safeCycleRings]);

  // Track independent scale/range for tall vs short waveform widgets while keeping timeline synced.
  const [shortWaveformRange, setShortWaveformRange] = useState<number | null>(null);
  const [waveformScaleOverride, setWaveformScaleOverride] = useState<TimeScale | null>(null);

  const ringSyncRecords = useMemo(
    () =>
      safeCycleRings.map((ring) => ({
        id: ring.id,
        name: (ring?.name ?? 'Unnamed'),
        parentId: ring?.parentId,
        stagesPerParent: ring?.stagesPerParent,
        stages: ring?.stages,
        waveform: ring?.waveform,
      })),
    [safeCycleRings]
  );

  const settingsWidgetMeta = useMemo(() => ({ id: 'settings', title: 'Settings' }), []);

  const getWidgetFixedDate = useCallback((widgetId: string): Date | null => {
    const raw = widgetCustomizations?.[widgetId]?.fixedTimestamp;
    if (!raw) return null;
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
  }, [widgetCustomizations]);

  const getDayOfYearFromDate = useCallback((date: Date): number => {
    const start = new Date(date.getFullYear(), 0, 0);
    const diff = date.getTime() - start.getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  }, []);

  const forceTimelineLinkedWidgets = true;

  const getWidgetDayOfYear = useCallback((widgetId: string, fallback: number): number => {
    const mode = forceTimelineLinkedWidgets ? 'linked' : (widgetSyncModes[widgetId] ?? 'linked');
    if (mode === 'isolated') {
      return isolatedWidgetValues[widgetId] ?? fallback;
    }
    if (mode === 'fixed') {
      const fixedDate = getWidgetFixedDate(widgetId);
      return fixedDate ? getDayOfYearFromDate(fixedDate) : fallback;
    }
    return fallback;
  }, [forceTimelineLinkedWidgets, getDayOfYearFromDate, getWidgetFixedDate, isolatedWidgetValues, widgetSyncModes]);

  const getWidgetGregorianDate = useCallback((widgetId: string): Date => {
    const mode = forceTimelineLinkedWidgets ? 'linked' : (widgetSyncModes[widgetId] ?? 'linked');
    if (mode === 'isolated') {
      return new Date(currentTime.getFullYear(), 0, (isolatedWidgetValues[widgetId] ?? 1));
    }
    if (mode === 'fixed') {
      return getWidgetFixedDate(widgetId) ?? (displayTime || currentTime);
    }
    return displayTime || currentTime;
  }, [currentTime, displayTime, forceTimelineLinkedWidgets, getWidgetFixedDate, isolatedWidgetValues, widgetSyncModes]);

  const agendaAnchorDate = useMemo(() => {
    const base = displayTime || currentTime;
    const normalized = new Date(base);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  }, [
    displayTime?.getFullYear(),
    displayTime?.getMonth(),
    displayTime?.getDate(),
    currentTime.getFullYear(),
    currentTime.getMonth(),
    currentTime.getDate(),
  ]);

  const reminderRange = useMemo(() => {
    const start = new Date(currentTime);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 2);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }, [currentTime.getFullYear(), currentTime.getMonth(), currentTime.getDate()]);

  const agendaEvents = useEventsForDay(agendaAnchorDate, viewId);
  const reminderEventsByDate = useEventsForRange(reminderRange.start, reminderRange.end, viewId);

  // Convert reminderEventsByDate object to array for RemindersWidget
  const reminderEvents = useMemo(() => {
    return Object.values(reminderEventsByDate).flat();
  }, [reminderEventsByDate]);

  const makeShortSummary = useCallback(
    (
      title: string,
      icon: ReactNode,
      lines: Array<string | ReactNode>,
      extras?: {
        progress?: number; // 0-100 percentage
        progressColor?: string;
        accent?: 'amber' | 'teal' | 'rose' | 'emerald';
      }
    ) => (
      <ShortWidgetWrapper title={title} icon={icon} accent={extras?.accent}>
        <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-center px-4 py-2 relative">
          {/* Decorative corner accents */}
          <div className="absolute top-1 left-2 w-4 h-4 pointer-events-none opacity-40">
            <svg viewBox="0 0 16 16" className="w-full h-full text-amber-400">
              <path d="M0,8 L0,0 L8,0" fill="none" stroke="currentColor" strokeWidth="1" />
            </svg>
          </div>
          <div className="absolute top-1 right-2 w-4 h-4 pointer-events-none opacity-40 rotate-90">
            <svg viewBox="0 0 16 16" className="w-full h-full text-amber-400">
              <path d="M0,8 L0,0 L8,0" fill="none" stroke="currentColor" strokeWidth="1" />
            </svg>
          </div>
          <div className="absolute bottom-1 left-2 w-4 h-4 pointer-events-none opacity-40 -rotate-90">
            <svg viewBox="0 0 16 16" className="w-full h-full text-amber-400">
              <path d="M0,8 L0,0 L8,0" fill="none" stroke="currentColor" strokeWidth="1" />
            </svg>
          </div>
          <div className="absolute bottom-1 right-2 w-4 h-4 pointer-events-none opacity-40 rotate-180">
            <svg viewBox="0 0 16 16" className="w-full h-full text-amber-400">
              <path d="M0,8 L0,0 L8,0" fill="none" stroke="currentColor" strokeWidth="1" />
            </svg>
          </div>
          
          {/* Icon with glow */}
          <div 
            className="text-amber-300"
            style={{ 
              filter: 'drop-shadow(0 0 8px rgba(244,208,63,0.4))',
            }}
          >
            {icon}
          </div>
          
          {/* Main content lines */}
          <div className="flex flex-col items-center gap-0.5">
            {lines.map((line, idx) => (
              <div 
                key={idx} 
                className={`leading-snug line-clamp-1 ${
                  idx === 0 
                    ? 'text-sm sm:text-base font-bold tracking-wide' 
                    : 'text-xs sm:text-sm text-amber-200/60 font-medium'
                }`}
                style={idx === 0 ? {
                  background: 'linear-gradient(135deg, #f4d03f 0%, #d4a574 50%, #c9a869 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                } : undefined}
              >
                {line}
              </div>
            ))}
          </div>
          
          {/* Optional progress bar with elegant styling */}
          {extras?.progress !== undefined && (
            <div className="w-full max-w-[140px] relative mt-1">
              {/* Track with inner shadow */}
              <div 
                className="h-1.5 rounded-full overflow-hidden"
                style={{
                  background: 'linear-gradient(180deg, rgba(13,42,46,0.8) 0%, rgba(20,60,65,0.6) 100%)',
                  boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.4)',
                }}
              >
                {/* Progress fill with glow */}
                <div 
                  className="h-full rounded-full transition-all duration-700 ease-out relative overflow-hidden"
                  style={{ 
                    width: `${Math.min(100, Math.max(0, extras.progress))}%`,
                    background: extras.progressColor || 'linear-gradient(90deg, #f4d03f, #d4a574)',
                    boxShadow: `0 0 10px ${extras.progressColor?.includes('7eb8b5') ? 'rgba(126,184,181,0.5)' : 'rgba(244,208,63,0.5)'}`,
                  }}
                >
                  {/* Shimmer overlay */}
                  <div 
                    className="absolute inset-0"
                    style={{
                      background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.25) 50%, transparent 100%)',
                      animation: 'shimmer 2.5s ease-in-out infinite',
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </ShortWidgetWrapper>
    ),
    []
  );

  const coreWidgets: WidgetConfig[] = useMemo(() => {
    try {
      return [
      {
        id: 'galactic-date',
        title: 'Galactic Time',
        icon: <Moon className="w-4 h-4" />, 
        defaultLayout: { x: 0, y: 0, w: 4, h: 3, minW: 3, minH: 2 },
        shortComponent: (() => {
          // Calculate moon progress through month (day/28)
          const moonProgress = displayDay?.isOutOfTime ? 100 : ((displayDay?.day ?? 1) / 28) * 100;
          return makeShortSummary(
            'Galactic Time',
            <Moon className="w-4 h-4" />,
            [
              displayDay?.isOutOfTime
                ? '✧ Day Out of Time ✧'
                : `${(allMonths?.[displayDay?.month - 1])?.name ?? 'Moon'} ${displayDay?.day ?? 0}`,
              `Year ${displayDay?.year ?? 0} • ${shouldUseLiveDay && liveGalacticDay
                ? `${String(liveGalacticDay?.hours ?? 0).padStart(2, '0')}:${String(liveGalacticDay?.minutes ?? 0).padStart(2, '0')}`
                : shownTime?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) ?? '--:--'}`,
            ],
            { progress: moonProgress, progressColor: `hsl(var(--month-${displayDay?.month ?? 1}))` }
          );
        })(),
        shortComponentTall: (
          <ShortWidgetWrapper title="Galactic Time" icon={<Moon className="w-4 h-4" />}>
            <div className="w-full h-full flex flex-col items-center justify-center gap-3 p-3">
              <MoonPhase gregorianDate={currentGregorianDate} size={48} showLabel={false} compact={true} />
              <div className="text-center">
                <div className="text-base font-bold text-amber-100">
                  {displayDay?.isOutOfTime ? (
                    <span className="text-cosmic-sun">✧ Day Out of Time ✧</span>
                  ) : (
                    <span style={{ color: `hsl(var(--month-${displayDay?.month ?? 1}))` }}>
                      {(allMonths?.[displayDay?.month - 1])?.name ?? 'Moon'} {displayDay?.day ?? 0}
                    </span>
                  )}
                </div>
                <div className="text-xl font-display font-bold cosmic-glow-text mt-1">
                  {shouldUseLiveDay && liveGalacticDay
                    ? `${String(liveGalacticDay?.hours ?? 0).padStart(2, '0')}:${String(liveGalacticDay?.minutes ?? 0).padStart(2, '0')}`
                    : shownTime?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) ?? '--:--'}
                </div>
                <div className="text-xs text-amber-200/70 mt-1">Year {displayDay?.year ?? 0}</div>
              </div>
            </div>
          </ShortWidgetWrapper>
        ),
        component: (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 h-full w-full md:max-w-4xl mx-auto p-2 sm:p-4">
            <MoonPhase gregorianDate={currentGregorianDate} size={48} showLabel={true} compact={true} />
            <div className="text-center flex-1 min-w-0">
              <p className="text-xs sm:text-sm text-muted-foreground mb-1">{isManualMode ? 'Viewing' : 'Now'} (Galactic)</p>
              <p className="text-lg sm:text-xl font-semibold leading-tight truncate">
                {displayDay?.isOutOfTime ? (
                  <span className="text-cosmic-sun">Day Out of Time ✨</span>
                ) : (
                  <span>
                    <span style={{ color: `hsl(var(--month-${displayDay?.month ?? 1}))` }}>
                      {(allMonths?.[displayDay?.month - 1])?.name ?? 'Month'}
                    </span>{' '}
                    {displayDay?.day ?? 0}, Year {displayDay?.year ?? 0}
                  </span>
                )}
              </p>
              <p className="text-2xl sm:text-3xl font-display font-bold cosmic-glow-text">
                {liveGalacticDay
                  ? `${String(liveGalacticDay?.hours ?? 0).padStart(2, '0')}:${String(liveGalacticDay?.minutes ?? 0).padStart(2, '0')}:${String(liveGalacticDay?.seconds ?? 0).padStart(2, '0')}`
                  : shownTime?.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: false,
                    })}
              </p>
            </div>
          </div>
        ),
      },
      {
        id: 'gregorian-date',
        title: 'Gregorian Date',
        icon: <Calendar className="w-4 h-4" />, 
        defaultLayout: { x: 4, y: 0, w: 3, h: 3, minW: 2, minH: 2 },
        shortComponent: (() => {
          // Calculate day progress through year
          const dayOfYear = currentGregorianDate && !isNaN(currentGregorianDate.getTime()) 
            ? Math.floor((currentGregorianDate.getTime() - new Date(currentGregorianDate.getFullYear(), 0, 0).getTime()) / (1000 * 60 * 60 * 24))
            : 1;
          const yearProgress = (dayOfYear / 365) * 100;
          return makeShortSummary(
            'Gregorian Date',
            <Calendar className="w-4 h-4" />,
            [
              currentGregorianDate && !isNaN(currentGregorianDate.getTime()) ? format(currentGregorianDate, 'EEEE') : '—',
              currentGregorianDate && !isNaN(currentGregorianDate.getTime()) ? format(currentGregorianDate, 'MMMM d, yyyy') : 'Select a date',
            ],
            { progress: yearProgress, progressColor: 'linear-gradient(90deg, #7eb8b5, #a8d4cf)' }
          );
        })(),
        shortComponentTall: (
          <ShortWidgetWrapper title="Gregorian Date" icon={<Calendar className="w-4 h-4" />} accent="teal">
            <div className="w-full h-full flex flex-col items-center justify-center gap-3 p-3">
              <Calendar className="w-10 h-10 text-teal-300" />
              <div className="text-center">
                <div className="text-sm font-medium text-teal-100">
                  {currentGregorianDate && !isNaN(currentGregorianDate.getTime()) ? format(currentGregorianDate, 'EEEE') : '—'}
                </div>
                <div className="text-lg font-semibold text-teal-50 mt-1">
                  {currentGregorianDate && !isNaN(currentGregorianDate.getTime()) ? format(currentGregorianDate, 'MMMM d, yyyy') : '—'}
                </div>
              </div>
            </div>
          </ShortWidgetWrapper>
        ),
        component: (
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 h-full w-full md:max-w-4xl mx-auto p-2 sm:p-4">
            <Calendar className="w-8 h-8 text-muted-foreground shrink-0" />
            <div className="text-center sm:text-left flex-1 min-w-0">
              <p className="text-sm sm:text-base font-medium truncate">{currentGregorianDate && !isNaN(currentGregorianDate.getTime()) ? format(currentGregorianDate, 'EEEE') : '—'}</p>
              <p className="text-lg sm:text-xl font-semibold truncate">{currentGregorianDate && !isNaN(currentGregorianDate.getTime()) ? format(currentGregorianDate, 'MMMM d, yyyy') : '—'}</p>
            </div>
          </div>
        ),
      },
      {
        id: 'timeline',
        title: 'Timeline (Gregorian)',
        icon: <Clock className="w-4 h-4" />, 
        defaultLayout: { x: 0, y: 0, w: 12, h: 3, minW: 8, minH: 2 },
        shortComponent: (() => {
          // Calculate hour progress through day
          const hourProgress = currentGregorianDate && !isNaN(currentGregorianDate.getTime())
            ? ((currentGregorianDate.getHours() * 60 + currentGregorianDate.getMinutes()) / (24 * 60)) * 100
            : 50;
          return makeShortSummary(
            'Timeline',
            <Clock className="w-4 h-4" />,
            [
              currentGregorianDate && !isNaN(currentGregorianDate.getTime()) ? format(currentGregorianDate, 'EEE, MMM d yyyy') : '—',
              currentGregorianDate && !isNaN(currentGregorianDate.getTime()) ? format(currentGregorianDate, 'HH:mm:ss') : 'Sync to now',
            ],
            { progress: hourProgress, progressColor: 'linear-gradient(90deg, #f4d03f, #c9a869)' }
          );
        })(),
        shortComponentTall: (
          <ShortWidgetWrapper title="Timeline" icon={<Clock className="w-4 h-4" />}>
            <div className="w-full h-full flex flex-col items-center justify-center gap-3 p-3">
              <Clock className="w-10 h-10 text-amber-300" />
              <div className="text-center">
                <div className="text-sm text-amber-200/70">{isManualMode ? 'Viewing' : 'Now'}</div>
                <div className="text-base font-semibold text-amber-100 mt-1">
                  {currentGregorianDate && !isNaN(currentGregorianDate.getTime()) ? format(currentGregorianDate, 'EEE, MMM d yyyy') : '—'}
                </div>
                <div className="text-xl font-display font-bold text-amber-50 mt-1">
                  {currentGregorianDate && !isNaN(currentGregorianDate.getTime()) ? format(currentGregorianDate, 'HH:mm:ss') : '—'}
                </div>
              </div>
            </div>
          </ShortWidgetWrapper>
        ),
        component: (
          <TimelineWidget
            currentGregorianDate={currentGregorianDate}
            isManualMode={isManualMode}
            handleSyncToNowFromTimeline={handleSyncToNowFromTimeline}
          />
        ),
      },
      {
        id: 'grid',
        title: selectedMonth === 14 ? 'The Drift' : `${currentMonth?.name || 'Moon'} - Grid`,
        icon: <Grid3X3 className="w-4 h-4" />,
        defaultLayout: { x: 0, y: 3, w: 7, h: 10, minW: 4, minH: 6 },
        shortComponent: (() => {
          if (selectedMonth === 14 || !currentMonth) {
            const dayProgress = currentMonth ? ((displayDay?.day ?? 1) / currentMonth.days.length) * 100 : 0;
            return makeShortSummary(
              selectedMonth === 14 ? 'The Drift' : `${currentMonth?.name || 'Moon'}`,
              <Grid3X3 className="w-4 h-4" />,
              [
                selectedMonth === 14
                  ? '✧ The Drift ✧'
                  : currentMonth
                    ? `Day ${displayDay?.day ?? 1} of ${currentMonth.days.length}`
                    : 'Loading...',
                selectedMonth === 14 ? 'Year transition portal' : `Moon ${selectedMonth} of 13`,
              ],
              selectedMonth !== 14 ? { progress: dayProgress, progressColor: `hsl(var(--month-${selectedMonth}))` } : undefined
            );
          }

          return (
            <ShortWidgetWrapper>
                <ShortGridWidget
                  currentMonth={currentMonth}
                  selectedDay={selectedDay}
                  displayDay={displayDay}
                  today={today}
                  selectedMonth={selectedMonth}
                  onSelectDay={handleSelectDayFromGrid}
                  dashboardId={viewId}
                  className="p-1"
                  variant="row"
                />
            </ShortWidgetWrapper>
          );
        })(),
        shortComponentTall: (() => {
          if (selectedMonth === 14 || !currentMonth) {
            return (
              <ShortWidgetWrapper title={selectedMonth === 14 ? 'The Drift' : currentMonth?.name || 'Moon'} icon={<Grid3X3 className="w-4 h-4" />}>
                <div className="w-full h-full flex flex-col items-center justify-center gap-3 p-3">
                  <Grid3X3 className="w-10 h-10 text-amber-300" />
                  <div className="text-center">
                    <div className="text-base font-semibold text-amber-100">
                      {selectedMonth === 14 ? '✧ The Drift ✧' : `${currentMonth?.name || 'Moon'}`}
                    </div>
                    <div className="text-xs text-amber-200/70 mt-1">
                      {selectedMonth === 14 ? 'Year transition portal' : `Moon ${selectedMonth} of 13`}
                    </div>
                  </div>
                </div>
              </ShortWidgetWrapper>
            );
          }

          return (
            <ShortWidgetWrapper>
            <ShortGridWidget
              currentMonth={currentMonth}
              selectedDay={selectedDay}
              displayDay={displayDay}
              today={today}
              selectedMonth={selectedMonth}
              onSelectDay={handleSelectDayFromGrid}
              dashboardId={viewId}
              className="p-2"
              variant="grid"
            />
            </ShortWidgetWrapper>
          );
        })(),
        component:
          selectedMonth === 14 ? (
            <DriftView year={selectedYear} config={config} onPrevMonth={prevMonth} onNextMonth={nextMonth} onSyncToDrift={handleSyncToDrift} />
          ) : currentMonth ? (
            <div className="group relative flex flex-col h-full w-full md:max-w-6xl mx-auto overflow-hidden">
              <MonthHeader
                month={currentMonth}
                year={selectedYear}
                currentDay={displayDay?.day ?? undefined}
                onPrevMonth={handleGridPrevMonth}
                onNextMonth={handleGridNextMonth}
              />
              <div className="flex-1 min-h-0">
                <MonthGrid
                  month={currentMonth}
                  today={today}
                  selectedDay={selectedDay}
                  onSelectDay={handleSelectDayFromGrid}
                  liveDay={indicatorDay && indicatorDay.month === currentMonth?.number ? indicatorDay.day : undefined}
                  dashboardId={viewId}
                />
              </div>
              {(selectedMonth === 1 || selectedMonth === 13) && (
                <div className="pointer-events-none absolute inset-x-0 bottom-2 z-20 flex justify-center opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-150">
                  <button
                    onClick={handleSyncToDriftFromGrid}
                    className="pointer-events-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/40 hover:bg-amber-500/30 hover:border-amber-500/60 transition-all cursor-pointer shadow-[0_6px_20px_rgba(0,0,0,0.25)] backdrop-blur-sm group"
                  >
                    <span className="text-amber-400 text-sm group-hover:scale-110 transition-transform">✧</span>
                    <span className="text-xs text-amber-200/90 font-medium">
                      {selectedMonth === 13 ? 'Next: The Drift →' : '← Prev: The Drift'}
                    </span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              Loading calendar...
            </div>
          ),
      },
      {
        id: 'gregorian-grid',
        title: `${gregorianViewMonth && !isNaN(gregorianViewMonth.getTime()) ? format(gregorianViewMonth, 'MMMM yyyy') : 'Gregorian'} - Gregorian`,
        icon: <Calendar className="w-4 h-4" />,
        defaultLayout: { x: 7, y: 4, w: 5, h: 8, minW: 4, minH: 5 },
        shortComponent: (() => {
          if (!gregorianViewMonth || isNaN(gregorianViewMonth.getTime())) {
            return makeShortSummary(
              'Gregorian',
              <Calendar className="w-4 h-4" />,
              ['Select a month', ''],
              { progress: 0, progressColor: 'linear-gradient(90deg, #7eb8b5, #a8d4cf)' }
            );
          }

          const year = gregorianViewMonth.getFullYear();
          const month = gregorianViewMonth.getMonth();
          const daysInMonth = new Date(year, month + 1, 0).getDate();
          const days = Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1));
          const selectedDate = getWidgetGregorianDate('gregorian-grid');
          const todayDate = indicatorDay?.gregorianDate || currentGregorianDate;
          const monthLabel = format(gregorianViewMonth, 'MMM yyyy');

          return (
            <GregorianShortCalendar
              monthLabel={monthLabel}
              days={days}
              selectedDate={selectedDate}
              todayDate={todayDate}
              onSelect={handleSelectGregorianDateFromGrid}
            />
          );
        })(),
        shortComponentTall: (() => {
          if (!gregorianViewMonth || isNaN(gregorianViewMonth.getTime())) {
            return (
              <ShortWidgetWrapper title="Gregorian" icon={<Calendar className="w-4 h-4" />} accent="teal">
                <div className="w-full h-full flex flex-col items-center justify-center gap-3 p-3">
                  <Calendar className="w-10 h-10 text-teal-300" />
                  <div className="text-center">
                    <div className="text-sm text-teal-200/70">Select a month</div>
                  </div>
                </div>
              </ShortWidgetWrapper>
            );
          }

          const year = gregorianViewMonth.getFullYear();
          const month = gregorianViewMonth.getMonth();
          const daysInMonth = new Date(year, month + 1, 0).getDate();
          const days = Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1));
          const selectedDate = getWidgetGregorianDate('gregorian-grid');
          const todayDate = indicatorDay?.gregorianDate || currentGregorianDate;
          const monthLabel = format(gregorianViewMonth, 'MMM yyyy');

          return (
            <GregorianShortCalendar
              monthLabel={monthLabel}
              days={days}
              selectedDate={selectedDate}
              todayDate={todayDate}
              onSelect={handleSelectGregorianDateFromGrid}
            />
          );
        })(),
        component: (() => {
          if (!gregorianViewMonth || isNaN(gregorianViewMonth.getTime())) {
            return (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                Loading calendar...
              </div>
            );
          }

          const selectedDate = getWidgetGregorianDate('gregorian-grid');
          const todayDate = indicatorDay?.gregorianDate || currentGregorianDate;
          const gregViewMonth = gregorianViewMonth.getMonth() + 1;

          return (
            <div className="mx-auto flex h-full w-full flex-col overflow-hidden">
              <GregorianGridHeader
                monthLabel={format(gregorianViewMonth, 'MMMM yyyy')}
                monthFraction={`Month ${gregViewMonth} of 12`}
                onPrev={handleGregorianPrevMonth}
                onNext={handleGregorianNextMonth}
              />
              <div className="flex-1 min-h-0">
                <GregorianGrid
                  currentDate={gregorianViewMonth}
                  selectedDate={selectedDate}
                  onSelectDate={handleSelectGregorianDateFromGrid}
                  liveDate={todayDate}
                  dashboardId={viewId}
                />
              </div>
            </div>
          );
        })(),
      },
      {
        id: 'agenda',
        title: 'Agenda',
        icon: <List className="w-4 h-4" />,
        defaultLayout: { x: 0, y: 12, w: 4, h: 6, minW: 3, minH: 4 },
        shortComponent: (
          <ShortWidgetWrapper title="Agenda" icon={<List className="w-4 h-4" />} accent="teal">
            <AgendaWidget events={agendaEvents} selectedDate={agendaAnchorDate} />
          </ShortWidgetWrapper>
        ),
        shortComponentTall: (
          <ShortWidgetWrapper title="Agenda" icon={<List className="w-4 h-4" />} accent="teal">
            <AgendaWidget events={agendaEvents} selectedDate={agendaAnchorDate} />
          </ShortWidgetWrapper>
        ),
        component: (
          <div className="w-full h-full">
            <AgendaWidget events={agendaEvents} selectedDate={agendaAnchorDate} />
          </div>
        ),
      },
      {
        id: 'reminders',
        title: 'Reminders',
        icon: <Bell className="w-4 h-4" />,
        defaultLayout: { x: 4, y: 12, w: 4, h: 6, minW: 3, minH: 4 },
        shortComponent: (
          <ShortWidgetWrapper title="Reminders" icon={<Bell className="w-4 h-4" />} accent="amber">
            <RemindersWidget events={reminderEvents} />
          </ShortWidgetWrapper>
        ),
        shortComponentTall: (
          <ShortWidgetWrapper title="Reminders" icon={<Bell className="w-4 h-4" />} accent="amber">
            <RemindersWidget events={reminderEvents} />
          </ShortWidgetWrapper>
        ),
        component: (
          <div className="w-full h-full">
            <RemindersWidget events={reminderEvents} />
          </div>
        ),
      },
      {
        id: 'legend',
        title: 'Cycle Legend',
        icon: <List className="w-4 h-4" />, 
        defaultLayout: { x: 7, y: 12, w: 5, h: 7, minW: 3, minH: 4 },
        shortComponent: (
          <ShortLegendWidget
            rings={safeCycleRings}
            hiddenRings={hiddenRings}
            onToggleRing={handleToggleRing}
          />
        ),
        shortComponentTall: (
          <ShortLegendWidget
            rings={safeCycleRings}
            hiddenRings={hiddenRings}
            onToggleRing={handleToggleRing}
          />
        ),
        component: (
          <div className="w-full h-full overflow-x-auto p-1 sm:p-2 md:p-3 bg-background/70 backdrop-blur-md border border-border/50 rounded-lg shadow-inner md:max-w-5xl mx-auto">
            <div className="min-w-[340px] sm:min-w-0">
              <CycleLegend
                viewId={viewId}
                rings={safeCycleRings}
                onToggleRing={isAdmin ? handleToggleRing : undefined}
                onAddRing={isAdmin ? handleAddRingFromPreset : undefined}
                onRemoveRing={isAdmin ? handleRemoveCycle : undefined}
                onEditRing={isAdmin ? handleEditCycle : undefined}
                onOpenWizard={isAdmin ? () => setShowCycleWizard(true) : undefined}
                onOpenThemeWizard={isAdmin ? () => setShowRingThemeWizard(true) : undefined}
                onReorderRings={isAdmin ? handleReorderRings : undefined}
                onSetSkinMode={isAdmin ? handleSetSkinMode : undefined}
                onSetVisibleCount={isAdmin ? setWidgetVisibleCount : undefined}
                hiddenRings={hiddenRings}
                ringThemes={ringThemeState.themes}
                visibleCount={widgetVisibleCount}
                className="text-xs sm:text-sm"
              />
            </div>
          </div>
        ),
      },
      {
        id: 'waveform',
        title: 'Cycle Waveforms',
        icon: <Activity className="w-4 h-4" />, 
        defaultLayout: { x: 7, y: 19, w: 5, h: 7, minW: 3, minH: 4 },
        shortComponent: (
          <ShortWaveformWidget
            rings={displayCycleRings}
            allRings={displayCycleRings}
            hiddenRings={hiddenRings}
            currentDayOfYear={waveformDayOfYear}
            year={selectedYear}
            onDayChange={handleWaveformChangeFromWidget}
            onInteractionStart={handleWaveformInteractionStart}
            onToggleRing={handleToggleRing}
            rangeDaysOverride={shortWaveformRange ?? undefined}
            onRangeChange={setShortWaveformRange}
          />
        ),
        shortComponentTall: (
          <ShortWaveformWidget
            rings={displayCycleRings}
            allRings={displayCycleRings}
            hiddenRings={hiddenRings}
            currentDayOfYear={waveformDayOfYear}
            year={selectedYear}
            onDayChange={handleWaveformChangeFromWidget}
            onInteractionStart={handleWaveformInteractionStart}
            onToggleRing={handleToggleRing}
            rangeDaysOverride={shortWaveformRange ?? undefined}
            onRangeChange={setShortWaveformRange}
          />
        ),
        component: (
          <div className="w-full h-full min-w-0 p-0 flex flex-col gap-2 overflow-hidden">
            <CycleGraph
              rings={displayCycleRings}
              allRings={displayCycleRings}
              currentDayOfYear={getWidgetDayOfYear('waveform', waveformDayOfYear)}
              year={selectedYear}
              onDayChange={handleWaveformChangeFromWidget}
              onInteractionStart={handleWaveformInteractionStart}
              onToggleRing={handleToggleRing}
              scaleOverride={waveformScaleOverride ?? undefined}
              onScaleChange={setWaveformScaleOverride}
              className="w-full h-full min-w-0"
            />
          </div>
        ),
      },
      {
        id: 'space-weather-waveforms',
        title: 'Space Weather Waveforms',
        icon: <Activity className="w-4 h-4" />,
        defaultLayout: { x: 0, y: 19, w: 7, h: 7, minW: 4, minH: 4 },
        shortComponent: makeShortSummary(
          'Space Weather Waveforms',
          <Activity className="w-4 h-4" />,
          [
            'Solar Activity Trends',
            'Real-time space weather patterns',
          ],
          { accent: 'teal' }
        ),
        shortComponentTall: (
          <ShortWidgetWrapper title="Space Weather Waveforms" icon={<Activity className="w-4 h-4" />} accent="teal">
            <div className="w-full h-full flex flex-col items-center justify-center gap-3 p-3">
              <div className="text-center">
                <div className="text-sm font-semibold text-teal-100 mb-1">Solar Activity</div>
                <div className="text-xs text-teal-200/70">Real-time space weather waveform data</div>
              </div>
              <div className="flex gap-2 items-center">
                <Activity className="w-6 h-6 text-teal-300" />
                <span className="text-xs text-teal-200/80">Live monitoring active</span>
              </div>
            </div>
          </ShortWidgetWrapper>
        ),
        component: (
          <div className="w-full h-full p-0 flex flex-col gap-2 overflow-hidden md:max-w-6xl mx-auto">
            <SpaceWeatherWaveformWidget />
          </div>
        ),
      },
      {
        id: 'cycles',
        title: 'Interlocking Cycles',
        icon: <Circle className="w-4 h-4" />,
        defaultLayout: { x: 0, y: 12, w: 7, h: 7, minW: 5, minH: 5 },
        shortComponent: (
          <ShortCyclesWidget
            rings={interlockingCycleRings}
            hiddenRings={hiddenRings}
            currentDayOfYear={getWidgetDayOfYear('cycles', waveformDayOfYear)}
            onSyncToNow={handleSyncToNowFromCycles}
            className="w-full h-full"
          />
        ),
        shortComponentTall: (
          <ShortCyclesWidget
            rings={interlockingCycleRings}
            hiddenRings={hiddenRings}
            currentDayOfYear={getWidgetDayOfYear('cycles', waveformDayOfYear)}
            onSyncToNow={handleSyncToNowFromCycles}
            className="w-full h-full"
          />
        ),
        component: (
          <div className="w-full h-full p-0 flex flex-col gap-2 overflow-hidden md:max-w-6xl mx-auto">
            <MultiRing
              viewId={viewId}
              rings={interlockingCycleRings}
              hiddenRings={hiddenRings}
              dashboardEditing={isAdmin}
              onRingsChange={handleRingsChange}
              onAddCycle={isAdmin ? () => setShowCycleWizard(true) : undefined}
              onRemoveCycle={handleRemoveCycle}
              onEditCycle={handleEditCycle}
              onOpenAlignment={
                isAdmin
                  ? (initialId) => {
                      setAlignmentInitialCycleId(initialId);
                      setShowAlignmentTool(true);
                    }
                  : undefined
              }
              toolbarActions={
                isAdmin
                  ? {
                      openWizard: () => setShowCycleWizard(true),
                      openTheme: () => setShowRingThemeWizard(true),
                      openAlignment: (initial) => {
                        setAlignmentInitialCycleId(initial);
                        setShowAlignmentTool(true);
                      },
                    }
                  : undefined
              }
              alignments={alignments}
              onSyncToNow={handleSyncToNowFromCycles}
              onRotationChange={handleRotationChangeFromCycles}
              gregorianDate={currentGregorianDate}
              maxRings={maxWidgetRings}
              ringThemeLayout={activeRingTheme ?? undefined}
              displayMode={cycleDisplayMode}
              onDisplayModeChange={onCycleDisplayModeChange}
              onVisibleCountChange={(count) => setWidgetVisibleCount(count)}
              onInteractionEnd={releaseWidgetMaster}
            />
          </div>
        ),
      },
      {
        id: 'ai-day-summary',
        title: 'ChatGPT AI Summary',
        icon: <Sparkles className="w-4 h-4" />, 
        defaultLayout: { x: 0, y: 22, w: 7, h: 6, minW: 4, minH: 4 },
        shortComponent: (() => {
          const visibleRingCount = displayCycleRings.length;
          return makeShortSummary(
            'AI Summary',
            <Sparkles className="w-4 h-4" />,
            [
              displayTime ? format(displayTime, 'MMMM d, yyyy') : format(currentTime, 'MMMM d, yyyy'),
              `Analyzing ${visibleRingCount} cycle${visibleRingCount !== 1 ? 's' : ''}`,
            ]
          );
        })(),
        shortComponentTall: (
          <ShortWidgetWrapper title="AI Summary" icon={<Sparkles className="w-4 h-4" />} accent="emerald">
            <div className="w-full h-full flex flex-col items-center justify-center gap-3 p-3">
              <Sparkles className="w-10 h-10 text-emerald-300" />
              <div className="text-center">
                <div className="text-sm font-semibold text-emerald-100">ChatGPT Analysis</div>
                <div className="text-xs text-emerald-200/70 mt-1">
                  {displayTime ? format(displayTime, 'MMMM d, yyyy') : format(currentTime, 'MMMM d, yyyy')}
                </div>
                <div className="text-xs text-emerald-200/70 mt-2">
                  Analyzing {displayCycleRings.length} cycle{displayCycleRings.length !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
          </ShortWidgetWrapper>
        ),
        component: (
          <div className="w-full h-full overflow-x-auto p-2 sm:p-4 md:max-w-5xl mx-auto">
            <div className="min-w-[340px] sm:min-w-0">
              <AiDaySummaryWidget
                rings={displayCycleRings}
                hiddenRings={hiddenRings}
                visibleCount={widgetVisibleCount ?? activeRingTheme?.visibleRingCount ?? undefined}
                currentDate={displayTime ?? currentTime}
                className="text-xs sm:text-sm"
              />
            </div>
          </div>
        ),
      },
      {
        id: 'year-ring',
        title: `Year ${selectedYear} - 13 Moons`,
        icon: <Circle className="w-4 h-4" />,
        defaultLayout: { x: 7, y: 0, w: 3, h: 4, minW: 3, minH: 3 },
        shortComponent: (() => {
          // Moon progress through year (13 moons)
          const moonProgress = selectedMonth === 14 ? 100 : ((selectedMonth) / 13) * 100;
          return makeShortSummary(
            `Year ${selectedYear}`,
            <Circle className="w-4 h-4" />,
            [
              selectedMonth === 14 ? '✧ The Drift ✧' : `Moon ${selectedMonth} of 13`,
              currentMonth?.name ?? (selectedMonth === 14 ? 'Year transition' : 'Loading...'),
            ],
            { progress: moonProgress, progressColor: `hsl(var(--month-${Math.min(selectedMonth, 13)}))` }
          );
        })(),
        shortComponentTall: (() => {
          const rawLiveMonth = liveGalacticDay
            ? liveGalacticDay.isOutOfTime ? 13 : liveGalacticDay.month
            : selectedMonth;
          const activeMonth = isYearRingSynced
            ? selectedMonth
            : (yearRingManualMonth ?? rawLiveMonth);
          return (
            <ShortWidgetWrapper title={`Year ${selectedYear}`} icon={<Circle className="w-4 h-4" />}>
              <div className="relative w-full h-full flex flex-col items-center justify-center gap-2 p-2">
                <button
                  type="button"
                  onClick={onToggleYearRingSync}
                  className="absolute top-1.5 right-1.5 z-30 flex h-6 w-6 items-center justify-center rounded-full border border-white/15 bg-black/30 text-white/50 backdrop-blur-sm transition-all hover:bg-white/10 hover:text-white/90"
                  title={isYearRingSynced ? 'Synced to timeline — click to unlink' : 'Not synced — click to link to timeline'}
                >
                  {isYearRingSynced ? <Link2 className="h-3 w-3" /> : <Link2Off className="h-3 w-3" />}
                </button>
                <YearRing
                  months={allMonths}
                  selectedMonth={activeMonth}
                  today={today}
                  onSelectMonth={isYearRingSynced ? () => {} : setYearRingManualMonth}
                  className="w-full max-w-[160px]"
                />
                <div className="text-center mt-1">
                  <div className="text-xs font-semibold text-amber-100">
                    {activeMonth === 14 ? '✧ The Drift ✧' : `Moon ${activeMonth} of 13`}
                  </div>
                  <div className="text-xs text-amber-200/70">
                    {allMonths[activeMonth - 1]?.name ?? (activeMonth === 14 ? 'Year transition' : 'Loading...')}
                  </div>
                </div>
              </div>
            </ShortWidgetWrapper>
          );
        })(),
        component: (() => {
          const rawLiveMonth = liveGalacticDay
            ? liveGalacticDay.isOutOfTime ? 13 : liveGalacticDay.month
            : selectedMonth;
          const liveMonth = isYearRingSynced
            ? selectedMonth
            : (yearRingManualMonth ?? rawLiveMonth);
          const liveMoonName = allMonths[liveMonth - 1]?.name ?? (liveMonth === 14 ? 'Year transition' : '');
          const liveMoonGlyph = allMonths[liveMonth - 1]?.glyph ?? '';

          // Tzolkin (Dreamspell): Kin 1 = July 26 1987
          const TZOLKIN_EPOCH = Date.UTC(1987, 6, 26);
          const now = currentGregorianDate ?? new Date();
          const daysSinceEpoch = Math.floor((Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) - TZOLKIN_EPOCH) / 86400000);
          const kinNumber = ((daysSinceEpoch % 260) + 260) % 260 + 1;
          const tone = ((kinNumber - 1) % 13) + 1;
          const seal = ((kinNumber - 1) % 20) + 1;
          const wavespell = Math.floor((kinNumber - 1) / 13) + 1;

          const TONE_NAMES = ['Magnetic','Lunar','Electric','Self-Existing','Overtone','Rhythmic','Resonant','Galactic','Solar','Planetary','Spectral','Crystal','Cosmic'];
          const SEAL_NAMES = ['Dragon','Wind','Night','Seed','Serpent','World-Bridger','Hand','Star','Moon','Dog','Monkey','Human','Skywalker','Wizard','Eagle','Warrior','Earth','Mirror','Storm','Sun'];
          const SEAL_GLYPHS = ['🐉','💨','🌙','🌱','🐍','💀','🤚','⭐','🌊','🐕','🐒','🧑','🚶','🧙','🦅','⚔️','🌍','🪞','🌀','☀️'];

          return (
            <div className="relative h-full w-full flex items-center justify-center gap-4 p-3">
              <button
                type="button"
                onClick={onToggleYearRingSync}
                className="absolute top-1.5 right-1.5 z-30 flex h-6 w-6 items-center justify-center rounded-full border border-white/15 bg-black/30 text-white/50 backdrop-blur-sm transition-all hover:bg-white/10 hover:text-white/90"
                title={isYearRingSynced ? 'Synced to timeline — click to unlink' : 'Not synced — click to link to timeline'}
              >
                {isYearRingSynced ? <Link2 className="h-3 w-3" /> : <Link2Off className="h-3 w-3" />}
              </button>
              {/* Year ring */}
              <div className="flex-shrink-0 flex flex-col items-center gap-2">
                <YearRing
                  months={allMonths}
                  selectedMonth={liveMonth}
                  today={today}
                  onSelectMonth={isYearRingSynced ? () => {} : setYearRingManualMonth}
                  className="w-full max-w-[220px]"
                />
                <div className="text-center">
                  <div className="text-xs font-semibold text-amber-100">
                    {liveMonth === 14 ? '✧ The Drift ✧' : `Moon ${liveMonth} of 13`}
                  </div>
                  <div className="text-xs text-amber-200/70">{liveMoonName}</div>
                </div>
              </div>
              {/* Mayan details panel */}
              <div className="flex flex-col gap-2 min-w-[130px]">
                <div className="rounded-lg bg-white/5 border border-white/10 p-2 text-center">
                  <div className="text-[10px] text-white/40 uppercase tracking-wide mb-0.5">Moon</div>
                  <div className="text-lg">{liveMoonGlyph}</div>
                  <div className="text-xs font-semibold text-amber-100">{liveMoonName}</div>
                </div>
                <div className="rounded-lg bg-white/5 border border-white/10 p-2 text-center">
                  <div className="text-[10px] text-white/40 uppercase tracking-wide mb-0.5">Tzolkin Kin</div>
                  <div className="text-base font-bold text-yellow-200">Kin {kinNumber}</div>
                </div>
                <div className="rounded-lg bg-white/5 border border-white/10 p-2 text-center">
                  <div className="text-[10px] text-white/40 uppercase tracking-wide mb-0.5">Solar Seal</div>
                  <div className="text-lg">{SEAL_GLYPHS[seal - 1]}</div>
                  <div className="text-xs font-semibold text-white/80">{SEAL_NAMES[seal - 1]}</div>
                </div>
                <div className="rounded-lg bg-white/5 border border-white/10 p-2 text-center">
                  <div className="text-[10px] text-white/40 uppercase tracking-wide mb-0.5">Galactic Tone</div>
                  <div className="text-sm font-semibold text-cyan-300">Tone {tone}</div>
                  <div className="text-xs text-white/60">{TONE_NAMES[tone - 1]}</div>
                </div>
                <div className="rounded-lg bg-white/5 border border-white/10 p-2 text-center">
                  <div className="text-[10px] text-white/40 uppercase tracking-wide mb-0.5">Wavespell</div>
                  <div className="text-sm font-semibold text-violet-300">#{wavespell}</div>
                </div>
              </div>
            </div>
          );
        })(),
      },
      {
        id: 'space-weather',
        title: 'Space Weather',
        icon: <span role="img" aria-label="space">🛰️</span>,
        defaultLayout: { x: 0, y: 30, w: 7, h: 6, minW: 3, minH: 3 },
        shortComponent: <ShortSpaceWeatherWidget />,
        shortComponentTall: <ShortTallSpaceWeatherWidget />,
        component: null, // will be set in DashboardPageImpl with isVisible
        alwaysShowInShort: true, // Ensure always included in scrollable short view if selected
      },
    ];
    } catch (error) {
      // Fallback: Return empty array if there's an error during widget initialization
      console.error('[useDashboardWidgets] Error initializing core widgets:', error);
      console.error('[useDashboardWidgets] Stack:', error instanceof Error ? error.stack : 'N/A');
      return [];
    }
  }, [
      activeRingTheme,
      agendaAnchorDate,
      agendaEvents,
      alignments,
      allMonths,
      config,
      currentGregorianDate,
      currentMonth,
      currentTime,
      displayDay,
      displayTime,
      gregorianViewMonth,
      handleAddRingFromPreset,
      handleEditCycle,
      handleGregorianNextMonth,
      handleGregorianPrevMonth,
      handleGridNextMonth,
      handleGridPrevMonth,
      handleReorderRings,
      handleRemoveCycle,
      handleRingsChange,
      handleRotationChangeFromCycles,
      handleSelectDayFromGrid,
      handleSelectGregorianDateFromGrid,
      handleSetSkinMode,
      handleSyncToDrift,
      handleSyncToDriftFromGrid,
      handleSyncToNowFromCycles,
      handleSyncToNowFromGrid,
      handleSyncToNowFromTimeline,
      handleToggleRing,
      handleWaveformChangeFromWidget,
      handleYearRingSelectMonth,
      isYearRingSynced,
      onToggleYearRingSync,
      yearRingManualMonth,
      setYearRingManualMonth,
      hiddenRings,
      displayCycleRings,
      interlockingCycleRings,
      indicatorDay,
      isAdmin,
      isManualMode,
      liveGalacticDay,
      maxWidgetRings,
      nextMonth,
      prevMonth,
      reminderEvents,
      releaseWidgetMaster,
      ringThemeState.themes,
      selectedDay,
      selectedGroupId,
      selectedMonth,
      selectedYear,
      setAlignmentInitialCycleId,
      setShowAlignmentTool,
      setShowCycleWizard,
      setShowRingThemeWizard,
      setWidgetVisibleCount,
      shouldUseLiveDay,
      shortWaveformRange,
      shownTime,
      today,
      waveformScaleOverride,
      viewId,
      waveformDayOfYear,
      widgetVisibleCount,
      cycleRings,
      isolatedWidgetValues,
      widgetSyncModes,
    ]
  );   

  const widgetSyncRecords = useMemo(
    () =>
      [...coreWidgets, settingsWidgetMeta]
        .filter(widget => widget && widget.id)
        .map((widget) => ({
          id: widget.id,
          title: widget?.title ?? 'Widget',
          syncMode: widgetSyncModes[widget.id] ?? 'linked',
        })),
    [coreWidgets, settingsWidgetMeta, widgetSyncModes]
  );


  // settingsWidgetMeta is already defined above; do not redeclare it here.

  const settingsWidget: WidgetConfig = useMemo(
    () => ({
      id: 'settings',
      title: 'Settings',
      icon: <Settings className="w-4 h-4" />, 
      defaultLayout: { x: 7, y: 26, w: 5, h: 4, minW: 2, minH: 3 },
      shortComponent: (
        <ShortWidgetWrapper title="Settings" icon={<Settings className="w-4 h-4" />}>
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-center px-2">
            <div className="flex flex-col items-center gap-0.5">
              <div className="text-sm sm:text-base font-semibold text-amber-100">Configuration</div>
              <div className="text-xs sm:text-sm text-amber-200/70">Year start • Sync options</div>
            </div>
          </div>
        </ShortWidgetWrapper>
      ),
      component: (
        <div className="w-full h-full p-2 sm:p-4 overflow-auto md:max-w-4xl mx-auto">
          <SettingsPanel
            config={config}
            onUpdateConfig={updateConfig}
            cycleDocId={cycleDocId}
            ringSyncRecords={ringSyncRecords}
            alignmentRecords={alignments}
            widgetSyncRecords={widgetSyncRecords}
            className="text-xs sm:text-sm"
          />
        </div>
      ),
    }),
    [alignments, config, cycleDocId, ringSyncRecords, updateConfig, widgetSyncRecords]
  );

  const allWidgets = useMemo(
    () => (isPaul ? [...coreWidgets, settingsWidget] : coreWidgets),
    [coreWidgets, isPaul, settingsWidget]
  );


  // Force baseline Dreamspell widgets to always be present in the resolved widget list.
  const forcedWidgetIds = ['gregorian-grid', 'grid', 'waveform', 'reminders', 'ai-day-summary', 'year-ring'];
  const mergedVisibleWidgets = useMemo(() => {
    const set = new Set(visibleWidgets);
    // Prepend forced widgets if missing
    const result = [...forcedWidgetIds.filter(id => !set.has(id)), ...visibleWidgets];
    return Array.from(new Set(result));
  }, [visibleWidgets]);

  // Preserve the order from `visibleWidgets` when resolving widget configs
  const activeWidgets = useMemo(() => {
    const byId = new Map(allWidgets.map((w) => [w.id, w]));
    return mergedVisibleWidgets.map((id) => byId.get(id)).filter(Boolean) as WidgetConfig[];
  }, [allWidgets, mergedVisibleWidgets]);

  // Widgets that can be added: those present in allWidgets but not currently visible
  const addableWidgets = useMemo(() => {
    const visibleSet = new Set(visibleWidgets);
    return allWidgets.filter((widget) => !visibleSet.has(widget.id));
  }, [allWidgets, visibleWidgets]);

  return {
    ringSyncRecords,
    settingsWidgetMeta,
    coreWidgets,
    widgetSyncRecords,
    settingsWidget,
    allWidgets,
    activeWidgets,
    addableWidgets,
  };
}
